import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TinyCard } from "@/components/Card3D";
import { GearSlots, TIER_COLOR } from "@/components/GearSlots";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { SLOT_META, RARITY_HEX, RTEXT } from "@/lib/inventoryVisuals";
import { toast } from "sonner";
import {
  Pickaxe, Hammer, Backpack, Zap, Sparkles, Swords, Shield, Gem, Loader2, Trash2, Compass,
  Leaf, Droplets, Footprints, TreePine, Star, FlaskConical, Wand2, Utensils, Gauge, ScrollText, Cpu, Plus,
  Search, Flame, Check, ChevronLeft, ChevronRight, X, Dices, TrendingUp, Award, ChevronDown, Lock,
  Sprout, Flower2, Fish, PawPrint, Diamond, Axe, Shell, Shovel, Telescope, Anvil, Cog, ChefHat, Wrench, Coins, Tag,
} from "lucide-react";

const ICONS = {
  pickaxe: Pickaxe, leaf: Leaf, sparkles: Sparkles, droplet: Droplets, footprints: Footprints,
  gem: Gem, "tree-pine": TreePine, shield: Shield, hammer: Hammer, star: Star,
  swords: Swords, "flask-conical": FlaskConical, "wand-sparkles": Wand2, utensils: Utensils,
  gauge: Gauge, "scroll-text": ScrollText, cpu: Cpu, flame: Flame,
  // realistic profession / material icons
  sprout: Sprout, flower: Flower2, fish: Fish, "paw-print": PawPrint, diamond: Diamond,
  axe: Axe, shell: Shell, shovel: Shovel, telescope: Telescope, anvil: Anvil, cog: Cog,
  "chef-hat": ChefHat, wrench: Wrench,
};
const Ic = ({ name, ...p }) => { const C = ICONS[name] || Sparkles; return <C {...p} />; };

const RARITY = {
  common: { ring: "ring-zinc-400/40", glow: "rgba(161,161,170,0.25)", text: "text-zinc-300" },
  uncommon: { ring: "ring-emerald-400/50", glow: "rgba(52,211,153,0.30)", text: "text-emerald-300" },
  rare: { ring: "ring-sky-400/50", glow: "rgba(56,189,248,0.32)", text: "text-sky-300" },
  epic: { ring: "ring-fuchsia-400/50", glow: "rgba(232,121,249,0.34)", text: "text-fuchsia-300" },
  legendary: { ring: "ring-amber-400/60", glow: "rgba(251,191,36,0.40)", text: "text-amber-300" },
  goddess: { ring: "ring-rose-400/70", glow: "rgba(251,113,133,0.45)", text: "text-rose-300" },
};
const SLOT_ICON = Object.fromEntries(Object.entries(SLOT_META).map(([key, meta]) => [key, meta.icon]));
// Suggested player gear sale price ≈ half a same-rarity card's base value.
const GEAR_SUGGEST = { common: 50, uncommon: 125, rare: 300, epic: 750, legendary: 2000, goddess: 5000 };

export default function AdventurePage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("gather");
  const [gatherCard, setGatherCard] = useState("");
  const [craftCard, setCraftCard] = useState("");

  const { data: state } = useQuery({ queryKey: ["adv-state"], queryFn: async () => (await api.get("/adventure/state")).data, refetchInterval: 30000 });
  const { data: profData } = useQuery({ queryKey: ["adv-prof"], queryFn: async () => (await api.get("/adventure/professions")).data });
  const { data: recipeData } = useQuery({ queryKey: ["adv-recipes"], queryFn: async () => (await api.get("/adventure/recipes")).data });
  const { data: cards } = useQuery({ queryKey: ["my-cards-adv"], queryFn: async () => (await api.get("/cards/mine")).data, select: (items) => items.filter((card) => !card.locked_out) });
  const { data: roster } = useQuery({ queryKey: ["adv-roster"], queryFn: async () => (await api.get("/adventure/roster")).data, refetchInterval: 30000 });

  const activeCardId = tab === "gather" ? gatherCard : craftCard;
  const activeCard = (cards || []).find((c) => c.id === activeCardId);
  const { data: status } = useQuery({
    queryKey: ["adv-card", activeCardId],
    queryFn: async () => (await api.get(`/adventure/card/${activeCardId}`)).data,
    enabled: !!activeCardId,
  });

  const profById = useMemo(() => {
    const m = {};
    [...(profData?.professions?.gathering || []), ...(profData?.professions?.crafting || [])].forEach((p) => (m[p.id] = p));
    return m;
  }, [profData]);

  // Gather All cost = 1c per gatherer card that can act now (never-worked cards are
  // at full stamina; worked cards use the roster's live can_gather flag).
  const gatherAllCost = useMemo(() => {
    const gathIds = new Set((profData?.professions?.gathering || []).map((p) => p.id));
    let n = 0;
    for (const c of cards || []) {
      if (!gathIds.has(c.profession)) continue;
      const r = roster?.cards?.[c.id];
      if (!r || r.can_gather) n++;
    }
    return n;
  }, [cards, roster, profData]);

  const refreshCard = () => {
    qc.invalidateQueries({ queryKey: ["adv-card", activeCardId] });
    qc.invalidateQueries({ queryKey: ["adv-roster"] });
    qc.invalidateQueries({ queryKey: ["adv-state"] });
  };
  const refreshAll = () => { refreshCard(); qc.invalidateQueries({ queryKey: ["my-cards-adv"] }); };

  const gather = useMutation({
    mutationFn: async (mat) => (await api.post("/adventure/gather", { mat, card_id: gatherCard })).data,
    onSuccess: (d) => {
      if (d.success && d.gained) toast.success(`+${d.gained.qty} ${d.gained.mat_name} · T${d.gained.tier}${d.leveled ? " · LEVEL UP!" : ""}`);
      else toast(`No luck this time — nothing gathered${d.leveled ? " · but LEVEL UP!" : ""}`, { icon: "🌫️" });
      refreshCard();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't gather")),
  });
  const gatherAll = useMutation({
    mutationFn: async () => (await api.post("/adventure/gather-all")).data,
    onSuccess: (d) => {
      const mats = Object.values(d.materials || {}).reduce((a, b) => a + b, 0);
      toast.success(`Gather All — ${d.cards_worked} card(s) worked · ${d.total_gathers} gathers · +${mats} materials · −${d.credits_spent}c`);
      refreshAll();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't gather all")),
  });
  const craft = useMutation({
    mutationFn: async (recipe_id) => (await api.post("/adventure/craft", { recipe_id, card_id: craftCard })).data,
    onSuccess: (d) => {
      const crit = d.item?.critical_forge;
      if (crit) {
        // Critical Forge (~2% chance) — every stat multiplied 2×–13×.
        // Use a long-duration celebratory toast so the moment lands.
        toast.success(
          `🔥 CRITICAL FORGE ×${crit.multiplier} — ${d.item.name}`,
          {
            duration: 10_000,
            description: `A ${crit.tier} craft! Every stat on this piece just got multiplied.`,
          },
        );
      } else {
        toast.success(`Forged ${d.item.name}!${d.leveled ? " · LEVEL UP!" : ""}`);
      }
      refreshAll();
    },
    onError: (e) => toast.error(apiError(e, "Couldn't craft")),
  });
  const equip = useMutation({
    mutationFn: async ({ item_id }) => (await api.post("/adventure/equip", { card_id: activeCardId, item_id })).data,
    onSuccess: () => { toast.success(`Equipped onto ${activeCard?.title || "card"}!`); refreshAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't equip")),
  });
  const scrap = useMutation({
    mutationFn: async (item_id) => (await api.post("/adventure/scrap", { card_id: "", item_id })).data,
    onSuccess: () => { toast.success("Scrapped"); refreshAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't scrap")),
  });
  const unequip = useMutation({
    mutationFn: async (item_id) => (await api.post("/adventure/unequip", { card_id: activeCardId, item_id })).data,
    onSuccess: () => { toast.success("Unequipped"); refreshAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't unequip")),
  });
  const listGear = useMutation({
    mutationFn: async ({ item_id, price }) => (await api.post("/gear/list", { item_id, price })).data,
    onSuccess: (d) => { toast.success(`Listed for ${d.price}c · you net ${d.net}c after fee`); refreshAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't list gear")),
  });
  const cancelGear = useMutation({
    mutationFn: async (listing_id) => (await api.post(`/gear/${listing_id}/cancel`)).data,
    onSuccess: () => { toast.success("Listing cancelled — gear is back in your bag"); refreshAll(); },
    onError: (e) => toast.error(apiError(e, "Couldn't cancel listing")),
  });

  const selectedCraftProf = craftCard ? profById[(cards || []).find((c) => c.id === craftCard)?.profession] : null;
  const selectedCraftProfId = craftCard ? (cards || []).find((c) => c.id === craftCard)?.profession : null;
  // Only surface recipes whose tier this card has actually unlocked. Locked tiers
  // are teased via a compact "next unlocks" chip strip below the grid rather than
  // filling the grid with disabled cards.
  const craftRecipes = useMemo(
    () => (recipeData?.recipes || [])
      .filter((r) => r.profession === selectedCraftProfId)
      .filter((r) => (r.level_req || 1) <= (status?.level || 1)),
    [recipeData, selectedCraftProfId, status?.level]
  );
  const lockedCraftTiers = useMemo(
    () => (recipeData?.recipes || [])
      .filter((r) => r.profession === selectedCraftProfId)
      .filter((r) => (r.level_req || 1) > (status?.level || 1))
      .sort((a, b) => a.tier - b.tier),
    [recipeData, selectedCraftProfId, status?.level]
  );

  const TABS = [
    { id: "gather", label: "Gather", icon: Pickaxe },
    { id: "craft", label: "Forge", icon: Hammer },
  ];

  const canAfford = (cost) => Object.entries(cost || {}).every(([k, q]) => (state?.mats?.[k] || 0) >= q);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-8" data-testid="adventure-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border border-amber-400/20 bg-[radial-gradient(120%_120%_at_0%_0%,rgba(251,191,36,0.10),transparent_55%)] p-6 mb-6">
        <Compass className="absolute -right-6 -top-6 h-40 w-40 text-amber-400/[0.06] rotate-12" />
        <div className="relative flex items-center gap-3">
          <span className="h-12 w-12 rounded-2xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
            <Compass className="h-6 w-6 text-amber-300" />
          </span>
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.04em] leading-none">ADVENTURE</h1>
            <p className="text-white/50 text-sm mt-1">Every card has its own <b className="text-amber-300/90">profession level</b> &amp; <b className="text-cyan-300/90">stamina</b> — level them up to gather faster, fail less, and pull richer materials.</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-5">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} data-testid={`adv-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`group relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all ${active ? "bg-amber-400 text-black shadow-[0_0_18px_rgba(251,191,36,0.3)]" : "bg-white/[0.04] text-white/60 hover:text-white border border-white/10"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* GATHER */}
      <div className="grid lg:grid-cols-[minmax(0,1fr)_330px] gap-6 items-start">
        <div className="min-w-0">
      {tab === "gather" && (
        <div data-testid="adv-gather">
          <div className="flex items-center justify-between gap-3 mb-3 rounded-2xl border border-amber-400/15 bg-amber-400/[0.04] px-4 py-3" data-testid="gather-all-bar">
            <div className="min-w-0">
              <div className="font-display text-base text-white tracking-wide">Gather All</div>
              <div className="text-white/45 text-xs mt-0.5">Send every rested gatherer to work until its stamina runs dry — <b className="text-amber-300/90">1c per card</b>.</div>
            </div>
            <Button data-testid="gather-all-button" onClick={() => gatherAll.mutate()} disabled={gatherAll.isPending || gatherAllCost === 0}
              className="shrink-0 bg-amber-400 text-black hover:bg-amber-300 font-semibold disabled:opacity-50">
              {gatherAll.isPending ? "Gathering…" : gatherAllCost === 0 ? "No rested gatherers" : (
                <span className="inline-flex items-center gap-1.5" data-testid="gather-all-cost">
                  Gather All · <Coins className="h-3.5 w-3.5" /> {gatherAllCost.toLocaleString()}c
                </span>
              )}
            </Button>
          </div>
          <CardPicker label="Send out a gatherer" cards={cards} value={gatherCard} onChange={setGatherCard}
            profById={profById} filter="gathering" testidPrefix="gather-picker" roster={roster} />

          {!gatherCard && (
            <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.02] p-8 text-center text-white/40 text-sm mb-6" data-testid="gather-empty">
              Pick a gatherer above to reveal the node it works.
            </div>
          )}

          {gatherCard && status?.kind === "gathering" && (
            <GatherPanel status={status} onGather={() => gather.mutate(status.mat)} pending={gather.isPending} />
          )}
        </div>
      )}

      {/* CRAFT */}
      {tab === "craft" && (
        <div data-testid="adv-craft">
          <CardPicker label="Forge with a crafter" cards={cards} value={craftCard} onChange={setCraftCard}
            profById={profById} filter="crafting" testidPrefix="craft-picker" roster={roster}
            recipes={recipeData?.recipes || []} mats={state?.mats || {}} />

          {!craftCard && (
            <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.02] p-8 text-center text-white/40 text-sm" data-testid="craft-empty">
              Pick a crafter above to reveal the items it can forge.
            </div>
          )}

          {craftCard && (
            <>
              {status?.kind === "crafting" && <CrafterStatus status={status} />}
              {selectedCraftProf && (
                <div className="text-xs text-fuchsia-300/80 mb-3 flex items-center gap-1.5" data-testid="craft-prof-label">
                  <Ic name={selectedCraftProf.icon} className="h-3.5 w-3.5" /> This {selectedCraftProf.name} can forge:
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {craftRecipes.map((r, i) => {
                  const affordable = canAfford(r.cost);
                  const noStam = status && !status.can_act;
                  // Unaffordable recipes blank out — only the (disabled) make button remains.
                  if (!affordable) {
                    return (
                      <motion.div key={r.recipe_id} data-testid={`recipe-${r.recipe_id}`}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                        className="rounded-2xl p-4 border border-white/8 bg-white/[0.015] flex flex-col">
                        <div className="flex items-center gap-2 mb-3 opacity-50">
                          <span className="h-8 w-8 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center shrink-0">
                            <Ic name={r.icon} className="h-4 w-4 text-white/40" />
                          </span>
                          <div className="text-white/55 text-sm font-medium truncate">{r.name}</div>
                          <span className="ml-auto font-mono text-[10px]" style={{ color: TIER_COLOR[r.tier] }}>T{r.tier}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mb-3 opacity-60">
                          {Object.entries(r.cost).map(([key, q]) => {
                            const [mk, tr] = key.split(":");
                            const have = state?.mats?.[key] || 0;
                            const short = have < q;
                            return (
                              <span key={key} title={`Have ${have}/${q} Tier ${tr} ${profData?.materials?.[mk]?.name || mk}`}
                                className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${short ? "border-rose-400/40 bg-rose-400/10" : "border-white/10 bg-black/40"}`}>
                                <Ic name={profData?.materials?.[mk]?.icon} className="h-3 w-3" style={{ color: TIER_COLOR[Number(tr)] }} />
                                <span className={`font-mono text-[10px] ${short ? "text-rose-300" : "text-white/60"}`}>{have}/{q}</span>
                                <span className="font-mono text-[9px]" style={{ color: TIER_COLOR[Number(tr)] }}>T{tr}</span>
                              </span>
                            );
                          })}
                        </div>
                        <Button size="sm" disabled data-testid={`craft-${r.recipe_id}`}
                          className="w-full h-7 mt-auto bg-white/5 text-white/40 text-xs font-medium disabled:opacity-100 cursor-not-allowed border border-white/10">
                          Need materials
                        </Button>
                      </motion.div>
                    );
                  }
                  return (
                    <motion.div key={r.recipe_id} data-testid={`recipe-${r.recipe_id}`}
                      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                      className="rounded-2xl p-4 border border-amber-400/25 bg-amber-400/[0.05] flex flex-col shadow-[0_0_18px_rgba(251,191,36,0.08)]">
                      <div className="flex items-start gap-3 mb-2">
                        <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${r.kind === "gear" ? "bg-cyan-400/10 border border-cyan-400/25" : "bg-fuchsia-400/10 border border-fuchsia-400/25"}`}>
                          <Ic name={r.icon} className={`h-5 w-5 ${r.kind === "gear" ? "text-cyan-300" : "text-fuchsia-300"}`} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-white font-medium text-sm leading-tight">{r.name}</div>
                          <div className="text-[10px] font-mono text-white/40 mt-0.5">{r.kind === "gear" ? r.slot : "consumable"} · T{r.tier}</div>
                        </div>
                      </div>
                      <div className="text-white/55 text-xs mb-2 leading-snug">{r.desc}</div>
                      <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        {Object.entries(r.cost).map(([key, q]) => {
                          const [mk, tr] = key.split(":");
                          const meta = profData?.materials?.[mk];
                          return (
                            <span key={key} title={`${q}× Tier ${tr} ${meta?.name || mk}`}
                              className="inline-flex items-center gap-1 rounded-md bg-black/40 border border-white/10 px-1.5 py-0.5">
                              <Ic name={meta?.icon} className="h-3 w-3" style={{ color: TIER_COLOR[Number(tr)] }} />
                              <span className="font-mono text-[10px] text-white/60">{q}</span>
                              <span className="font-mono text-[9px]" style={{ color: TIER_COLOR[Number(tr)] }}>T{tr}</span>
                            </span>
                          );
                        })}
                        {status?.cost != null && (
                          <span className="inline-flex items-center gap-0.5 rounded-md bg-cyan-400/10 border border-cyan-400/25 px-1.5 py-0.5">
                            <Zap className="h-3 w-3 text-cyan-300" /><span className="font-mono text-[10px] text-cyan-200">{status.cost}</span>
                          </span>
                        )}
                      </div>
                      <Button size="sm" data-testid={`craft-${r.recipe_id}`} disabled={craft.isPending || noStam} onClick={() => craft.mutate(r.recipe_id)}
                        className="w-full h-7 mt-auto bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-40 text-xs font-medium">
                        {craft.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : noStam ? "Too tired" : <><Hammer className="h-3 w-3 mr-1" /> Forge</>}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
              {lockedCraftTiers.length > 0 && (
                <div data-testid="craft-next-unlocks" className="mt-4 rounded-xl border border-white/8 bg-white/[0.02] px-3 py-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase text-white/40">
                    <Lock className="h-3 w-3" /> Next unlocks
                  </span>
                  {lockedCraftTiers.map((r) => (
                    <span key={r.recipe_id} data-testid={`craft-locked-t${r.tier}`}
                      className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-black/40 px-1.5 py-0.5">
                      <span className="font-mono text-[10px]" style={{ color: TIER_COLOR[r.tier] }}>T{r.tier}</span>
                      <span className="font-mono text-[10px] text-white/50">· Lv {r.level_req}</span>
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* SATCHEL — under the card area only (constrained to the main column, a touch smaller) */}
      <div className="mt-6">
        <Satchel mats={state?.mats} materials={profData?.materials}
          highlight={tab === "craft" && craftCard ? new Set(selectedCraftProf?.mats || []) : null} />
      </div>
        </div>{/* end main column */}

        {/* SIDEBAR — active card's equip slots + compact bag */}
        <aside className="space-y-4 lg:sticky lg:top-4" data-testid="adv-sidebar">
          {activeCard ? (
            <div className="rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="adv-loadout">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45 mb-2.5 truncate">{activeCard.title} · Slots</div>
              <GearSlots gear={activeCard.gear} loadout={activeCard.loadout} onUnequip={(it) => unequip.mutate(it.id)} />
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-white/12 bg-white/[0.02] p-4 text-center text-white/35 text-xs" data-testid="adv-loadout-empty">Pick a card above to see its equip slots.</div>
          )}
          <InventoryShelf compact kindLabel="Gear" icon={Shield} accent="#22d3ee" filterType="slot"
            items={state?.gear || []} cards={cards} empty="No gear yet — forge some."
            equipLabel="Equip" equipTarget={activeCard?.title} canEquip={!!activeCardId} testidPrefix="gear-shelf"
            onEquip={(it) => activeCardId ? equip.mutate({ item_id: it.id }) : toast.error("Pick a card above to equip onto")}
            onList={(item_id, price) => listGear.mutate({ item_id, price })}
            onCancelList={(listing_id) => cancelGear.mutate(listing_id)}
            onScrap={(it) => scrap.mutate(it.id)} />
          <InventoryShelf compact kindLabel="Consumables" icon={FlaskConical} accent="#e879f9" filterType="rarity"
            items={state?.consumables || []} cards={cards} empty="No consumables yet — brew some."
            equipLabel="Slot" equipTarget={activeCard?.title} canEquip={!!activeCardId} testidPrefix="consumable-shelf"
            onEquip={(it) => activeCardId ? equip.mutate({ item_id: it.id }) : toast.error("Pick a card above to slot onto")}
            onScrap={(it) => scrap.mutate(it.id)} />
        </aside>
      </div>{/* end grid */}
    </div>
  );
}

// ── small bars ───────────────────────────────────────────────────────────────
function Bar({ pct, from, to, glow, height = "h-2" }) {
  return (
    <div className={`flex-1 ${height} rounded-full bg-black/50 overflow-hidden border border-white/10`}>
      <motion.div className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${from}, ${to})`, boxShadow: glow }}
        animate={{ width: `${Math.max(0, Math.min(100, pct))}%` }} transition={{ ease: "easeOut", duration: 0.5 }} />
    </div>
  );
}

function LevelXP({ status }) {
  const xpPct = status.maxed ? 100 : Math.round((status.xp / Math.max(1, status.xp_to_next)) * 100);
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex items-center gap-1 shrink-0 rounded-lg bg-amber-400/12 border border-amber-400/30 px-2 py-1">
        <TrendingUp className="h-3.5 w-3.5 text-amber-300" />
        <span className="font-mono text-xs text-amber-200" data-testid="adv-card-level">Lv {status.level}{status.maxed ? " · MAX" : ""}</span>
      </span>
      <Bar pct={xpPct} from="#f59e0b" to="#fde68a" glow="0 0 10px rgba(251,191,36,0.4)" />
      <span className="font-mono text-[10px] text-white/45 tabular-nums shrink-0">{status.maxed ? "MAX" : `${status.xp}/${status.xp_to_next} xp`}</span>
    </div>
  );
}

function Stamina({ status }) {
  const pct = Math.round((status.stamina / status.stamina_max) * 100);
  const tired = status.cost != null && status.stamina < status.cost;
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex items-center gap-1 shrink-0 rounded-lg bg-cyan-400/12 border border-cyan-400/30 px-2 py-1">
        <Zap className={`h-3.5 w-3.5 ${tired ? "text-white/30" : "text-cyan-300"}`} />
        <span className="font-mono text-xs text-cyan-200" data-testid="adv-card-stamina">{status.stamina}/{status.stamina_max}</span>
      </span>
      <Bar pct={pct} from={tired ? "#52525b" : "#22d3ee"} to={tired ? "#71717a" : "#67e8f9"} glow="0 0 10px rgba(34,211,238,0.4)" />
      {status.cost != null && <span className="font-mono text-[10px] text-white/45 tabular-nums shrink-0">−{status.cost}/use</span>}
    </div>
  );
}

function GatherPanel({ status, onGather, pending }) {
  const odds = status.tier_odds || {};
  const tired = !status.can_act;
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-amber-400/40 bg-[radial-gradient(120%_120%_at_50%_0%,rgba(251,191,36,0.10),transparent_60%)] p-6 mb-7"
      data-testid={`gather-node-${status.mat}`}>
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Node */}
        <div className="flex flex-col items-center text-center sm:w-48 shrink-0">
          <span className="relative h-20 w-20 rounded-full flex items-center justify-center mb-3"
            style={{ background: "radial-gradient(circle,rgba(251,191,36,0.28),transparent 70%)" }}>
            {!tired && <span className="absolute inset-0 rounded-full bg-amber-400/20 animate-ping" />}
            <Ic name={status.icon} className="relative h-10 w-10 text-amber-300" />
          </span>
          <div className="text-white font-display text-xl tracking-wide">{status.node}</div>
          <div className="text-amber-300/80 text-sm mt-0.5">Harvest <b>{status.mat_name}</b></div>
          <Button data-testid={`gather-btn-${status.mat}`} disabled={pending || tired} onClick={onGather}
            className="mt-4 h-10 px-6 bg-amber-400 text-black hover:bg-amber-300 disabled:opacity-40 font-medium">
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Pickaxe className="h-4 w-4 mr-1.5" /> {tired ? "Too tired" : <>Gather · <Zap className="h-3.5 w-3.5 mx-1" /> {status.cost}</>}</>}
          </Button>
        </div>

        {/* Live data */}
        <div className="flex-1 space-y-3 min-w-0">
          <LevelXP status={status} />
          <Stamina status={status} />
          <div className="rounded-xl border border-white/10 bg-black/30 p-3 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45"><Dices className="h-3.5 w-3.5" /> Gather odds</span>
              <span className="text-xs">Success <b className="text-emerald-300" data-testid="adv-success-chance">{Math.round(status.success_chance * 100)}%</b></span>
            </div>
            <div className="space-y-1.5">
              {[1, 2, 3, 4, 5].map((t) => (
                <div key={t} className="flex items-center gap-2" data-testid={`adv-odds-t${t}`}>
                  <span className="font-mono text-[10px] w-6 shrink-0" style={{ color: TIER_COLOR[t] }}>T{t}</span>
                  <Bar pct={Math.round((odds[t] || 0) * 100)} from={TIER_COLOR[t]} to={TIER_COLOR[t]} glow="none" height="h-1.5" />
                  <span className="font-mono text-[10px] text-white/55 tabular-nums w-9 text-right">{Math.round((odds[t] || 0) * 100)}%</span>
                </div>
              ))}
            </div>
            <div className="text-[10px] text-white/40 pt-0.5">Yields {status.yield_min}–{status.yield_max} per success · {Math.round(status.success_chance * 100)}% chance to find anything. Level up to fail less &amp; pull higher tiers.</div>
          </div>
          <PerkLadder perks={status.perks} />
        </div>
      </div>
    </motion.div>
  );
}

function CrafterStatus({ status }) {
  return (
    <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-400/[0.04] p-4 mb-4 space-y-3" data-testid="crafter-status">
      <LevelXP status={status} />
      <Stamina status={status} />
      <PerkLadder perks={status.perks} />
    </div>
  );
}

// ── Per-level perk ladder — every level 2-20 grants something new ─────────────
function PerkLadder({ perks }) {
  const [open, setOpen] = useState(false);
  if (!perks?.length) return null;
  const unlocked = perks.filter((p) => p.unlocked);
  const next = perks.find((p) => !p.unlocked);
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid="perk-ladder">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2" data-testid="perk-ladder-toggle">
        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
          <Award className="h-3.5 w-3.5 text-amber-300" /> Level Perks · <b className="text-amber-200">{unlocked.length}/{perks.length}</b>
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {!open && next && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-400/25 bg-amber-400/[0.06] px-2.5 py-1.5" data-testid="perk-next">
          <Lock className="h-3.5 w-3.5 text-amber-300/70 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-amber-200 text-xs font-medium">Next: Lv {next.level} — {next.name}</div>
            <div className="text-white/55 text-[11px] leading-snug">{next.desc}</div>
          </div>
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-1 max-h-60 overflow-y-auto pr-1">
          {perks.map((p) => (
            <div key={p.level} data-testid={`perk-${p.level}`}
              className={`flex items-start gap-2 rounded-lg px-2 py-1.5 border ${p.unlocked ? "bg-emerald-400/[0.05] border-emerald-400/15" : "bg-white/[0.02] border-white/8 opacity-60"}`}>
              <span className={`font-mono text-[10px] w-9 shrink-0 mt-0.5 ${p.unlocked ? "text-emerald-300" : "text-white/35"}`}>Lv {p.level}</span>
              <div className="min-w-0 flex-1">
                <div className={`text-xs font-medium ${p.unlocked ? "text-white" : "text-white/50"}`}>{p.name}</div>
                <div className="text-[11px] text-white/45 leading-snug">{p.desc}</div>
              </div>
              {p.unlocked ? <Check className="h-3.5 w-3.5 text-emerald-300 shrink-0 mt-0.5" /> : <Lock className="h-3 w-3 text-white/25 shrink-0 mt-0.5" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Satchel — tier shelves ─────────────────────────────────────────────────────
const TIER_META = {
  1: { label: "Tier I", color: "#67e8f9", glow: "rgba(103,232,249,0.16)" },
  2: { label: "Tier II", color: "#c084fc", glow: "rgba(192,132,252,0.18)" },
  3: { label: "Tier III", color: "#fbbf24", glow: "rgba(251,191,36,0.22)" },
  4: { label: "Tier IV", color: "#fb7185", glow: "rgba(251,113,133,0.22)" },
  5: { label: "Tier V", color: "#e879f9", glow: "rgba(232,121,249,0.24)" },
};

function Satchel({ mats, materials, highlight }) {
  const byTier = useMemo(() => {
    const o = { 1: [], 2: [], 3: [], 4: [], 5: [] };
    Object.entries(mats || {}).forEach(([k, n]) => {
      if (!n) return;
      const [mat, tier] = k.split(":");
      const t = Number(tier);
      if (o[t]) o[t].push({ key: k, mat, tier: t, n, meta: materials?.[mat] });
    });
    Object.values(o).forEach((arr) => arr.sort((a, b) => b.n - a.n));
    return o;
  }, [mats, materials]);
  const grand = Object.values(mats || {}).reduce((a, b) => a + (b || 0), 0);
  const kinds = new Set(Object.keys(mats || {}).filter((k) => mats[k] > 0).map((k) => k.split(":")[0])).size;
  const hasHl = highlight && highlight.size > 0;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4" data-testid="satchel">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-9 w-9 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center"><Backpack className="h-4.5 w-4.5 text-amber-300" /></span>
        <div className="mr-auto">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">Satchel</div>
          <div className="text-white text-sm" data-testid="satchel-total">{grand} material{grand === 1 ? "" : "s"}{grand ? ` · ${kinds} kind${kinds === 1 ? "" : "s"}` : ""}</div>
        </div>
        {hasHl && (
          <span className="flex items-center gap-1.5 text-[11px] text-amber-300/90" data-testid="satchel-used-hint">
            <span className="h-2 w-2 rounded-full bg-amber-300 animate-pulse" /> glowing = used by this crafter
          </span>
        )}
      </div>
      {grand === 0 ? (
        <div className="text-white/35 text-xs py-8 text-center">Empty — send a gatherer to a node to start filling it.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((t) => {
            const tm = TIER_META[t];
            const items = byTier[t];
            const tierTotal = items.reduce((a, b) => a + b.n, 0);
            return (
              <div key={t} data-testid={`satchel-tier-${t}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                style={{ boxShadow: `inset 0 0 30px ${tm.glow}` }}>
                <div className="flex items-center justify-between mb-2.5">
                  <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: tm.color }}>
                    <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((d) => <span key={d} className="h-1.5 w-1.5 rounded-full" style={{ background: d <= t ? tm.color : "rgba(255,255,255,0.15)" }} />)}</span>
                    {tm.label}
                  </span>
                  <span className="font-mono text-[10px] text-white/40 tabular-nums">{tierTotal}</span>
                </div>
                {items.length === 0 ? (
                  <div className="text-white/25 text-[11px] py-5 text-center">No Tier {t} mats yet</div>
                ) : (
                  <div className="grid grid-cols-3 gap-1.5">
                    {items.map((it) => {
                      const used = highlight?.has(it.mat);
                      const name = it.meta?.name || it.mat;
                      return (
                        <div key={it.key} data-testid={`mat-${it.key}`} title={name}
                          className={`group relative aspect-square rounded-lg border transition-all flex flex-col items-center justify-center gap-0.5 ${used ? "border-amber-300/70 bg-amber-400/[0.10]" : "border-white/[0.08] bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]"}`}
                          style={used ? { boxShadow: "0 0 14px rgba(251,191,36,0.45), inset 0 0 12px rgba(251,191,36,0.10)" } : undefined}>
                          {used && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-300 shadow-[0_0_8px_rgba(251,191,36,0.9)] animate-pulse z-10" />}
                          <Ic name={it.meta?.icon} className="h-5 w-5" style={{ color: used ? "#fcd34d" : tm.color }} />
                          <span className="font-mono text-[10px] tabular-nums" style={{ color: used ? "#fcd34d" : tm.color }}>×{it.n}</span>
                          {/* Hover label — floats above the tile with the mat name */}
                          <span data-testid={`mat-label-${it.key}`}
                            className="pointer-events-none absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap rounded-md border border-white/15 bg-black/90 px-2 py-0.5 text-[10px] capitalize text-white shadow-lg z-20">
                            {name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Card picker — collapses to just the chosen card once selected ──────────────
const PICK_RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const RARITY_RANK = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, goddess: 5 };
const PICK_PAGE = 12;

function MiniStamina({ adv }) {
  if (!adv) return null;
  const pct = Math.round((adv.stamina / (adv.stamina_max || 100)) * 100);
  return (
    <div className="mt-1 h-1 rounded-full bg-black/60 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#22d3ee,#67e8f9)" }} />
    </div>
  );
}

function CardPicker({ label, cards, value, onChange, profById, filter, testidPrefix = "card-picker", roster, recipes = [], mats = {} }) {
  const [search, setSearch] = useState("");
  const [profFilter, setProfFilter] = useState("all");
  const [rarity, setRarity] = useState("all");
  const [page, setPage] = useState(0);

  const advFor = (id) => roster?.cards?.[id] || { level: 1, stamina: roster?.stamina_max || 100, stamina_max: roster?.stamina_max || 100 };

  const kindList = useMemo(() => {
    let l = cards || [];
    if (filter) l = l.filter((c) => { const p = profById?.[c.profession]; return p && (filter === "gathering" ? !!p.mat : !p.mat); });
    return l;
  }, [cards, profById, filter]);

  const profOptions = useMemo(() => {
    const seen = new Map();
    kindList.forEach((c) => { const p = profById?.[c.profession]; if (p && !seen.has(p.id)) seen.set(p.id, p); });
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [kindList, profById]);

  const eligible = useMemo(() => {
    let l = kindList;
    // Gather area: hide any gatherer too tired to actually gather (stamina < its gather cost).
    if (filter === "gathering") l = l.filter((c) => { const a = roster?.cards?.[c.id]; return !a || a.can_gather !== false; });
    // Forge area: only show crafters who can actually make something right now
    // (enough stamina AND enough materials for at least one of their recipes).
    if (filter === "crafting") l = l.filter((c) => {
      const a = roster?.cards?.[c.id];
      if (a && a.can_craft === false) return false;
      if (!recipes.length) return true; // recipes still loading — don't blank the picker
      return recipes.some((r) => r.profession === c.profession && Object.entries(r.cost || {}).every(([k, q]) => (mats[k] || 0) >= q));
    });
    return l;
  }, [kindList, filter, roster, recipes, mats]);

  const filtered = useMemo(() => {
    let l = eligible;
    if (profFilter !== "all") l = l.filter((c) => c.profession === profFilter);
    if (rarity !== "all") l = l.filter((c) => c.rarity === rarity);
    if (search.trim()) { const q = search.trim().toLowerCase(); l = l.filter((c) => (c.title || "").toLowerCase().includes(q)); }
    return [...l].sort((a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0) || (a.title || "").localeCompare(b.title || ""));
  }, [eligible, profFilter, rarity, search]);

  useEffect(() => { setPage(0); }, [profFilter, rarity, search, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PICK_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * PICK_PAGE, safePage * PICK_PAGE + PICK_PAGE);
  const selectedCard = (cards || []).find((c) => c.id === value);

  // COLLAPSED — only the chosen card is shown; tap it (or ✕) to choose another.
  if (selectedCard) {
    const p = profById?.[selectedCard.profession];
    const adv = advFor(selectedCard.id);
    return (
      <div className="mb-5 rounded-2xl border border-amber-400/40 bg-amber-400/[0.05] p-4" data-testid={`${testidPrefix}-collapsed`}>
        <div className="flex items-center gap-4">
          <button data-testid={`${testidPrefix}-change`} onClick={() => onChange("")}
            className="relative w-16 shrink-0 rounded-lg overflow-hidden ring-2 ring-amber-300/60 group">
            {selectedCard.front_image && <img src={selectedCard.front_image} alt={selectedCard.title} className="w-full aspect-[3/4] object-cover" />}
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
              <X className="h-5 w-5 text-white" />
            </span>
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium truncate">{selectedCard.title}</span>
              <span className={`text-[10px] font-mono uppercase ${RARITY[selectedCard.rarity]?.text || "text-zinc-300"}`}>{selectedCard.rarity}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-cyan-200/80 mt-0.5">
              <Ic name={p?.icon} className="h-3.5 w-3.5" /> {p?.name || selectedCard.profession}
              <span className="text-white/35">· Lv {adv.level}</span>
            </div>
            <button onClick={() => onChange("")} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-amber-300/90 hover:text-amber-200">
              <X className="h-3 w-3" /> Choose another card
            </button>
          </div>
        </div>
      </div>
    );
  }

  // EXPANDED — filters, art grid + pagination.
  return (
    <div className="mb-5 rounded-2xl border border-white/10 bg-black/30 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
        <label className="text-white/45 text-[10px] uppercase tracking-[0.25em] sm:mr-auto">{label}</label>
        <div className="relative flex-1 sm:max-w-[190px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
          <Input data-testid={`${testidPrefix}-search`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cards…" className="h-9 pl-8 bg-[hsl(220_18%_12%)] border-white/10 text-sm" />
        </div>
        <Select value={profFilter} onValueChange={setProfFilter}>
          <SelectTrigger data-testid={`${testidPrefix}-prof-filter`} className="h-9 w-full sm:w-[160px] bg-[hsl(220_18%_12%)] border-white/10 text-sm"><SelectValue placeholder="All professions" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all" data-testid={`${testidPrefix}-prof-all`}>All professions</SelectItem>
            {profOptions.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`${testidPrefix}-prof-${p.id}`}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={rarity} onValueChange={setRarity}>
          <SelectTrigger data-testid={`${testidPrefix}-rarity-filter`} className="h-9 w-full sm:w-[130px] bg-[hsl(220_18%_12%)] border-white/10 text-sm capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>{PICK_RARITIES.map((r) => <SelectItem key={r} value={r} className="capitalize text-sm">{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {pageItems.length === 0 ? (
        <div className="text-white/35 text-xs py-8 text-center" data-testid={`${testidPrefix}-no-cards`}>No matching cards.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
          {pageItems.map((c) => {
            const p = profById?.[c.profession];
            const adv = advFor(c.id);
            return (
              <div key={c.id} data-testid={`${testidPrefix}-opt-${c.id}`} className="rounded-xl">
                <TinyCard card={c} idKey={c.id} previewSide="top" onClick={() => onChange(c.id)}
                  footer={
                    <div className="mt-1 px-0.5">
                      <div className="text-white text-[11px] font-medium truncate">{c.title}</div>
                      <div className="flex items-center justify-between gap-1 text-[10px] text-cyan-200/80 mt-0.5">
                        <span className="flex items-center gap-1 truncate"><Ic name={p?.icon} className="h-3 w-3 shrink-0" /> {p?.name || "—"}</span>
                        <span className="font-mono text-amber-200/80 shrink-0">L{adv.level}</span>
                      </div>
                      <MiniStamina adv={adv} />
                    </div>
                  }
                />
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > PICK_PAGE && (
        <div className="flex items-center justify-between mt-3">
          <span className="font-mono text-[10px] text-white/35">{filtered.length} card{filtered.length === 1 ? "" : "s"} · page {safePage + 1}/{totalPages}</span>
          <div className="flex gap-2">
            <Button data-testid={`${testidPrefix}-prev-page`} size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
            <Button data-testid={`${testidPrefix}-next-page`} size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

const SLOT_FILTERS = ["all", "weapon", "armor", "trinket", "relic"];
const RARITY_FILTERS = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];
const SHELF_PAGE = 8;

function InventoryShelf({ kindLabel, icon: Icon, accent, filterType, items, empty, onEquip, onScrap, onList, onCancelList, canEquip, equipLabel, equipTarget, testidPrefix, compact, cards }) {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [listingFor, setListingFor] = useState(null);
  const [listPrice, setListPrice] = useState("");
  const pageSize = compact ? 4 : SHELF_PAGE;
  const options = filterType === "slot" ? SLOT_FILTERS : RARITY_FILTERS;
  const titleById = useMemo(() => Object.fromEntries((cards || []).map((c) => [c.id, c.title])), [cards]);

  const filtered = useMemo(() => {
    let l = items || [];
    if (filter !== "all") l = l.filter((it) => (filterType === "slot" ? it.slot : it.rarity) === filter);
    return [...l].sort((a, b) => (b.equipped_on ? 1 : 0) - (a.equipped_on ? 1 : 0) || (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0));
  }, [items, filter, filterType]);

  useEffect(() => { setPage(0); }, [filter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-3.5" data-testid={testidPrefix}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}1a`, border: `1px solid ${accent}40` }}>
          <Icon className="h-4 w-4" style={{ color: accent }} />
        </span>
        <div className="mr-auto min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">{kindLabel}</div>
          <div className="text-white/60 text-[11px] truncate" data-testid={`${testidPrefix}-count`}>
            {filtered.length} item{filtered.length === 1 ? "" : "s"}{canEquip ? ` · → ${equipTarget}` : ""}
          </div>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid={`${testidPrefix}-filter`} className="h-7 w-[120px] bg-[hsl(220_18%_12%)] border-white/10 text-xs capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="capitalize text-sm">{o}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {pageItems.length === 0 ? (
        <div className="text-white/35 text-xs py-5 text-center" data-testid={`${testidPrefix}-empty`}>{filtered.length === 0 && filter === "all" ? empty : "Nothing matches this filter."}</div>
      ) : (
        <div className={`grid gap-2 ${compact ? "grid-cols-1" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`}>
          {pageItems.map((it, i) => {
            const r = RARITY[it.rarity] || RARITY.common;
            const SI = it.kind === "gear" ? (SLOT_ICON[it.slot] || Swords) : FlaskConical;
            const equippedTitle = it.equipped_on ? titleById[it.equipped_on] : null;
            return (
              <motion.div key={it.id} data-testid={`item-${it.id}`} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.02 }}
                className={`group relative rounded-xl border bg-black/40 p-2.5 ring-1 ${r.ring} border-white/10`} style={{ boxShadow: `inset 0 0 18px ${r.glow}` }}>
                {it.equipped_on && (
                  <span className="absolute top-1.5 right-1.5 flex items-center gap-0.5 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-1.5 py-0.5 text-[8px] font-mono text-emerald-300" data-testid={`equipped-badge-${it.id}`}>
                    <Check className="h-2.5 w-2.5" /> {equippedTitle ? "on " + equippedTitle.split(" ")[0] : "equipped"}
                  </span>
                )}
                <div className="flex items-start gap-2">
                  <span className="h-9 w-9 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center shrink-0">
                    <InventoryIcon item={it} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <InventoryName item={it} className="block text-xs truncate" />
                    <div className={`text-[10px] font-mono uppercase ${r.text}`}>{it.rarity} · <span style={{ color: TIER_COLOR[it.tier] }}>T{it.tier}</span>{it.slot ? ` · ${it.slot}` : ""}</div>
                    {it.critical_forge && (
                      <div className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300/60 bg-amber-400/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-amber-200"
                        title={`Critical Forge — every stat multiplied ×${it.critical_forge.multiplier}`}
                        data-testid={`gear-crit-${it.id}`}>
                        ★ Crit ×{it.critical_forge.multiplier}
                      </div>
                    )}
                  </div>
                </div>
                <div className="font-mono text-[11px] text-white/60 mt-1 leading-snug line-clamp-2">{it.desc}</div>
                {it.listed ? (
                  <div className="flex items-center gap-1.5 mt-2" data-testid={`gear-listed-${it.id}`}>
                    <span className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md border border-amber-400/40 bg-amber-400/10 text-[10px] font-mono text-amber-300"><Tag className="h-3 w-3" /> Listed</span>
                    {onCancelList && (
                      <Button size="sm" data-testid={`gear-cancel-list-${it.id}`} onClick={() => onCancelList(it.market_listing_id)}
                        className="h-7 px-2 bg-rose-400/90 text-black hover:bg-rose-300 text-[10px]">Cancel</Button>
                    )}
                  </div>
                ) : listingFor === it.id ? (
                  <div className="flex items-center gap-1.5 mt-2" data-testid={`gear-list-row-${it.id}`}>
                    <Input type="number" min={1} value={listPrice} autoFocus
                      data-testid={`gear-list-price-${it.id}`}
                      onChange={(e) => setListPrice(e.target.value)}
                      className="h-7 flex-1 bg-black/40 border-white/10 text-white text-[11px] font-mono px-2" placeholder="Price" />
                    <Button size="sm" data-testid={`gear-list-confirm-${it.id}`} disabled={!listPrice || Number(listPrice) < 1}
                      onClick={() => { onList(it.id, Number(listPrice)); setListingFor(null); setListPrice(""); }}
                      className="h-7 px-2 bg-emerald-400 text-black hover:bg-emerald-300 text-[10px] disabled:opacity-40">List</Button>
                    <button data-testid={`gear-list-cancel-${it.id}`} onClick={() => { setListingFor(null); setListPrice(""); }}
                      className="h-7 w-7 rounded-md border border-white/10 text-white/40 hover:text-white flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex gap-1.5 mt-2">
                    <Button size="sm" data-testid={`equip-${it.id}`} disabled={!canEquip} onClick={() => onEquip(it)}
                      className="flex-1 h-7 bg-cyan-400/90 text-black hover:bg-cyan-300 text-[11px] disabled:opacity-40">
                      <Plus className="h-3 w-3 mr-0.5" /> {equipLabel}
                    </Button>
                    {onList && it.kind === "gear" && (
                      <button data-testid={`gear-sell-${it.id}`} onClick={() => { setListingFor(it.id); setListPrice(String(GEAR_SUGGEST[it.rarity] || 50)); }} title="Sell on market"
                        className="h-7 w-7 rounded-md border border-white/10 text-white/30 hover:text-amber-300 hover:border-amber-400/40 flex items-center justify-center shrink-0"><Coins className="h-3.5 w-3.5" /></button>
                    )}
                    <button data-testid={`scrap-${it.id}`} onClick={() => onScrap(it)} title="Scrap"
                      className="h-7 w-7 rounded-md border border-white/10 text-white/30 hover:text-rose-300 hover:border-rose-400/40 flex items-center justify-center shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {filtered.length > pageSize && (
        <div className="flex items-center justify-between mt-3">
          <span className="font-mono text-[10px] text-white/35">page {safePage + 1}/{totalPages}</span>
          <div className="flex gap-2">
            <Button data-testid={`${testidPrefix}-prev`} size="sm" variant="outline" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} className="border-white/15 text-white/70 h-7"><ChevronLeft className="h-4 w-4" /></Button>
            <Button data-testid={`${testidPrefix}-next`} size="sm" variant="outline" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)} className="border-white/15 text-white/70 h-7"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
